import React, { useCallback, useMemo, useRef, useState } from 'react';

import { getStringWithoutHash } from 'utilitys/helpers/general';

import useTableCurrentState from 'hooks/useTableCurrentState';

import { useQueryProjectScoreCardsSummary } from 'api/queries/getProjectScoreCardsSummary';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import MainTable from 'components/common/Tables/MainTable/MainTable';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { prepareVersionSelectTitle } from 'components/common/TreeProgramFilter/VersionSelect/versionSelect.utils';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import classes from 'components/Dashboards/Portfolio/PortfolioOverview/PortfolioOverview.module.scss';
import { serializeScoreCardsData } from 'components/Dashboards/Portfolio/PortfolioOverview/helper';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { columns, initialState } from 'components/widgets/myProjectScoreCard/utils';

const scoreCardId = 'score-card';

export const MyProjectScoreCardWidget = () => {
    const tableRef = useRef(null);
    const {
        data: scoreCards,
        isLoading: isLoadingScoreCards,
        isFetching: isFetchingScoreCards,
    } = useQueryProjectScoreCardsSummary({
        filter: {
            filters: [],
        },
    });
    const componentKey = 'myProjectScorecards';
    const title = 'My Projects';
    const subTitleForExport = `${title}, ${prepareVersionSelectTitle()}`;
    const scoreCardsTableData = useMemo(() => {
        return scoreCards ? serializeScoreCardsData(scoreCards?.componentsList) : [];
    }, [scoreCards]);
    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);
    const updatedInitialState = useTableCurrentState({ initialState, componentKey });
    const handleInstance = useCallback((instance) => (tableRef.current = instance), []);

    return (
        <WidgetWithTitle
            title={<WidgetTitle title={title} />}
            id={scoreCardId}
            titleComponents={[
                <ExportDropdown title={title} key={'export'} subTitle={subTitleForExport} />,
                tableRef?.current && (
                    <ColumnsManager
                        onOpenModal={() => setModalColumnsOpen(true)}
                        onHideModal={() => setModalColumnsOpen(false)}
                        showModal={modalColumnsOpen}
                        componentKey={componentKey}
                        tableInstance={tableRef.current}
                        initialState={initialState}
                        columns={columns}
                    />
                ),
                <TooltipIcon key={'info'} tooltip={scoreCards?.info} />,
                <ThreeDotsMenu
                    widgetId={undefined}
                    componentKey={componentKey}
                    shareTitle={title}
                    shareLink={`${getStringWithoutHash(window.location.href)}#${scoreCardId}`}
                />,
            ]}
        >
            <div data-testid={'table-container'} className={classes.tableContainer}>
                {updatedInitialState && (
                    <MainTable
                        getInstanceCallback={handleInstance}
                        initialState={updatedInitialState}
                        columns={columns}
                        data={scoreCardsTableData}
                        maxHeight={500}
                        isLoading={isLoadingScoreCards || isFetchingScoreCards}
                    />
                )}

                {tableRef?.current && (
                    <CustomTableColumnsModal
                        onHideModal={() => setModalColumnsOpen(false)}
                        showModal={modalColumnsOpen}
                        componentKey={componentKey}
                        tableInstance={tableRef.current}
                        initialState={initialState}
                        columns={columns}
                    />
                )}
            </div>
        </WidgetWithTitle>
    );
};
